// ─── Inventory Page ───────────────────────────────────────────────────────────
// Piece-wise stock straight from purchase entries: every purchase line with
// its tag SKU, vendor, size/collection, pricing, and how many pieces have
// been sold vs how many remain. Searchable + groupable.
import React, { useState, useEffect, useMemo } from 'react';
import { purchaseAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Layers, RefreshCw, Search, ChevronDown, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Package, ShoppingBag, Boxes, IndianRupee, Download, Globe } from 'lucide-react';

const fmt0 = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GROUP_OPTIONS = [
  { value: '',            label: 'No Grouping' },
  { value: 'vendor',      label: 'Vendor' },
  { value: 'itemName',    label: 'Item' },
  { value: 'category',    label: 'Category' },
  { value: 'collection',  label: 'Collection' },
  { value: 'productSize', label: 'Size' },
  { value: 'productStatus', label: 'Status' },
];

export default function InventoryPage() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [groupBy, setGroupBy]   = useState('');
  const [hideSoldOut, setHideSoldOut] = useState(false);
  const [showWebsiteSoldOnly, setShowWebsiteSoldOnly] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = async () => {
    setLoading(true);
    try {
      // Inventory never shows images — skip the ~200MB base64 payload for speed
      const r = await purchaseAPI.getItemView({ includeImages: false });
      setRows(r.data.data || []);
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Reset page when search, grouping or filters change
  useEffect(() => {
    setPage(1);
  }, [search, groupBy, hideSoldOut, showWebsiteSoldOnly]);

  // Remaining pieces per line
  const enriched = useMemo(() => rows.map(r => {
    const purchased = Number(r.quantity || 0);
    const sold = Math.min(purchased, Number(r.soldQty || 0));
    const websiteSold = Math.min(purchased, Number(r.websiteSoldQty || 0));
    return { ...r, purchased, sold, websiteSold, remaining: Math.max(0, purchased - sold) };
  }), [rows]);

  const totalWebsiteSoldPieces = useMemo(() => enriched.reduce((s, r) => s + (r.websiteSold || 0), 0), [enriched]);
  const websiteSoldRowsCount = useMemo(() => enriched.filter(r => (r.websiteSold || 0) > 0).length, [enriched]);

  // Search across the visible fields
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = enriched;
    if (hideSoldOut) out = out.filter(r => r.remaining > 0);
    if (showWebsiteSoldOnly) out = out.filter(r => (r.websiteSold || 0) > 0);
    if (!q) return out;
    return out.filter(r =>
      [r.skuNo, r.itemName, r.vendor, r.category, r.collection, r.productSize, r.purityCode, r.purchaseNo, r.productStatus]
        .some(v => v && String(v).toLowerCase().includes(q)));
  }, [enriched, search, hideSoldOut, showWebsiteSoldOnly]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(Math.max(1, page), totalPages);

  const paginatedFiltered = useMemo(() => {
    if (pageSize <= 0 || pageSize >= filtered.length) return filtered;
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  // Group rows by the chosen field (using paginated rows)
  const groups = useMemo(() => {
    if (!groupBy) return [{ key: '', rows: paginatedFiltered }];
    const map = new Map();
    for (const r of paginatedFiltered) {
      const key = String(r[groupBy] || '—');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, rs]) => ({ key, rows: rs }));
  }, [paginatedFiltered, groupBy]);

  // Stats over the filtered set
  const totPurchased = filtered.reduce((s, r) => s + r.purchased, 0);
  const totSold      = filtered.reduce((s, r) => s + r.sold, 0);
  const totRemaining = filtered.reduce((s, r) => s + r.remaining, 0);
  const purchaseValue = filtered.reduce((s, r) => s + r.purchased * Number(r.purchasePrice || 0), 0);
  const totSaleValue = filtered.reduce((s, r) => s + r.purchased * Number(r.salePrice || 0), 0);
  const stockValue   = filtered.reduce((s, r) => s + r.remaining * Number(r.salePrice || 0), 0);

  const exportExcel = async () => {
    if (!filtered || filtered.length === 0) {
      toast.error('No inventory data to export');
      return;
    }
    try {
      const XLSX = await import('xlsx');
      const dataRows = filtered.map(r => ({
        'SKU No': r.skuNo || '—',
        'Item Name': r.itemName || '',
        'Category': r.category || '',
        'Purity': r.purityCode || '',
        'Purchase No': r.purchaseNo || '',
        'Vendor': r.vendor || '',
        'Size': r.productSize || '',
        'Collection': r.collection || '',
        'Purchased Qty': r.purchased,
        'Sold Qty': r.sold,
        'In Stock Qty': r.remaining,
        'Purchase Price (₹)': Number(r.purchasePrice || 0),
        'Total Purch (₹)': r.purchased * Number(r.purchasePrice || 0),
        'Total Purch 3% (₹)': r.purchased * Number(r.purchasePrice || 0) * 1.03,
        'MRP (₹)': Number(r.mrp || 0),
        'Discount (₹)': Number(r.discount || 0),
        'Sale Price (₹)': Number(r.salePrice || 0),
        'Total Sale (₹)': r.purchased * Number(r.salePrice || 0),
      }));

      // Totals Row
      dataRows.push({
        'SKU No': 'Grand Total',
        'Item Name': '',
        'Category': '',
        'Purity': '',
        'Purchase No': '',
        'Vendor': '',
        'Size': '',
        'Collection': '',
        'Purchased Qty': totPurchased,
        'Sold Qty': totSold,
        'In Stock Qty': totRemaining,
        'Purchase Price (₹)': '',
        'Total Purch (₹)': purchaseValue,
        'Total Purch 3% (₹)': purchaseValue * 1.03,
        'MRP (₹)': '',
        'Discount (₹)': '',
        'Sale Price (₹)': '',
        'Total Sale (₹)': totSaleValue,
      });

      const ws = XLSX.utils.json_to_sheet(dataRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
      XLSX.writeFile(wb, `inventory-stock-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Inventory exported to Excel successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Excel');
    }
  };

  const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  // ── Cell renderers ─────────────────────────────────────────
  const RemainBadge = ({ r }) => {
    const cls = r.remaining <= 0 ? { bg: '#fee2e2', fg: '#dc2626' }
      : r.remaining <= 2 ? { bg: '#fef3c7', fg: '#b45309' }
      : { bg: '#dcfce7', fg: '#15803d' };
    return (
      <span style={{ display: 'inline-block', minWidth: 34, textAlign: 'center', background: cls.bg, color: cls.fg, fontWeight: 800, fontSize: 12.5, padding: '3px 10px', borderRadius: 999 }}>
        {r.remaining <= 0 ? 'Sold out' : fmt0(r.remaining)}
      </span>
    );
  };

  const th = { padding: '8px 8px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--silver-500)', whiteSpace: 'nowrap', textAlign: 'left', borderBottom: '1px solid var(--border)', background: '#fbfcfe', position: 'sticky', top: 0 };
  const thR = { ...th, textAlign: 'right' };
  const td = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
  const tdR = { ...td, textAlign: 'right', fontFamily: 'var(--mono)' };

  const GroupHeader = ({ g }) => {
    const gp = g.rows.reduce((s, r) => s + r.purchased, 0);
    const gs = g.rows.reduce((s, r) => s + r.sold, 0);
    const gr = g.rows.reduce((s, r) => s + r.remaining, 0);
    const gpv = g.rows.reduce((s, r) => s + r.purchased * Number(r.purchasePrice || 0), 0);
    const gtotSale = g.rows.reduce((s, r) => s + r.purchased * Number(r.salePrice || 0), 0);
    const isCollapsed = collapsed[g.key];
    const tdG = { padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 12.5, fontWeight: 800, color: 'var(--navy-800, #1e293b)', whiteSpace: 'nowrap' };
    const tdGR = { ...tdG, textAlign: 'right', fontFamily: 'var(--mono)' };
    return (
      <tr onClick={() => toggle(g.key)} style={{ cursor: 'pointer', background: 'var(--silver-100, #f1f5f9)' }}>
        <td colSpan={5} style={tdG}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
            <span style={{ fontWeight: 800, fontSize: 13.5 }}>{g.key}</span>
            <span className="badge badge-navy" style={{ fontSize: 10.5 }}>{g.rows.length} line{g.rows.length > 1 ? 's' : ''}</span>
          </div>
        </td>
        <td style={tdGR}>{fmt0(gp)}</td>
        <td style={{ ...tdGR, color: '#b45309' }}>{fmt0(gs)}</td>
        <td style={{ ...tdG, textAlign: 'center', color: '#15803d' }}>{fmt0(gr)}</td>
        <td style={tdGR}>—</td>
        <td style={tdGR}>₹{fmt2(gpv)}</td>
        <td style={{ ...tdGR, color: '#1e40af' }}>₹{fmt2(gpv * 1.03)}</td>
        <td style={tdGR}>—</td>
        <td style={tdGR}>—</td>
        <td style={tdGR}>—</td>
        <td style={{ ...tdGR, color: '#be185d' }}>₹{fmt2(gtotSale)}</td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex-between mb-4">
        <div><h1>Inventory</h1><p className="text-muted">Piece-wise stock from purchase entries — purchased, sold &amp; remaining</p></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            className="btn"
            style={{
              background: showWebsiteSoldOnly ? '#2563eb' : '#eff6ff',
              color: showWebsiteSoldOnly ? '#fff' : '#1d4ed8',
              border: '1.5px solid ' + (showWebsiteSoldOnly ? '#1d4ed8' : '#93c5fd'),
              display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700,
              padding: '0 16px', borderRadius: 8, height: 38, cursor: 'pointer',
              boxShadow: showWebsiteSoldOnly ? '0 2px 6px rgba(37,99,235,0.3)' : 'none',
              transition: 'all 0.15s ease-in-out'
            }}
            onClick={() => setShowWebsiteSoldOnly(v => !v)}
            title="Filter items sold or ordered via website"
          >
            <Globe size={15} /> Website Sold ({totalWebsiteSoldPieces})
          </button>
          <button className="btn btn-ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
          <button className="btn" style={{ background: '#10b981', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, padding: '0 16px', borderRadius: 8, height: 38, cursor: 'pointer' }}
            onClick={exportExcel} disabled={filtered.length === 0}>
            <Download size={15} /> Export Excel
          </button>
        </div>
      </div>

      {/* ── Summary ── */}
      <div className="stat-grid mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="stat-card blue">
          <div className="stat-label"><Package size={13} style={{ verticalAlign: -2 }} /> Purchased</div>
          <div className="stat-value">{fmt0(totPurchased)}</div>
          <div className="stat-sub">Pieces bought</div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label"><ShoppingBag size={13} style={{ verticalAlign: -2 }} /> Sold</div>
          <div className="stat-value">
            {fmt0(totSold)}
            {totalWebsiteSoldPieces > 0 && (
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb', marginLeft: 6 }}>
                ({fmt0(totalWebsiteSoldPieces)} web)
              </span>
            )}
          </div>
          <div className="stat-sub">
            {totalWebsiteSoldPieces > 0 ? `${fmt0(totSold - totalWebsiteSoldPieces)} counter + ${fmt0(totalWebsiteSoldPieces)} website` : 'Pieces sold'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><Boxes size={13} style={{ verticalAlign: -2 }} /> In Stock</div>
          <div className="stat-value" style={{ color: '#15803d' }}>{fmt0(totRemaining)}</div>
          <div className="stat-sub">Pieces remaining</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><IndianRupee size={13} style={{ verticalAlign: -2 }} /> Purchase Value (3%)</div>
          <div className="stat-value" style={{ fontSize: '1.2rem', color: '#1e40af' }}>₹{fmt0(purchaseValue * 1.03)}</div>
          <div className="stat-sub">Purchased × purchase price (3%)</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><IndianRupee size={13} style={{ verticalAlign: -2 }} /> Total Sale Value</div>
          <div className="stat-value" style={{ fontSize: '1.2rem', color: '#be185d' }}>₹{fmt0(totSaleValue)}</div>
          <div className="stat-sub">Purchased × sale price</div>
        </div>
        <div className="stat-card">
          <div className="stat-label"><IndianRupee size={13} style={{ verticalAlign: -2 }} /> Stock Value</div>
          <div className="stat-value" style={{ fontSize: '1.2rem', color: '#15803d' }}>₹{fmt0(stockValue)}</div>
          <div className="stat-sub">Remaining × sale price</div>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="card mb-4" style={{ overflow: 'visible' }}>
        <div className="card-body" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: 280 }}>
              <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--silver-400, #94a3b8)' }} />
              <input className="form-control" style={{ paddingLeft: 34 }}
                placeholder="Search SKU, item, vendor, size…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label className="form-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Group by</label>
              <select className="form-control" style={{ width: 160 }} value={groupBy}
                onChange={e => { setGroupBy(e.target.value); setCollapsed({}); }}>
                {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--navy-800, #1e293b)' }}>
              <input type="checkbox" checked={hideSoldOut} onChange={e => setHideSoldOut(e.target.checked)} />
              Hide sold-out
            </label>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              color: showWebsiteSoldOnly ? '#1d4ed8' : '#475569',
              background: showWebsiteSoldOnly ? '#eff6ff' : '#f8fafc',
              border: '1px solid ' + (showWebsiteSoldOnly ? '#93c5fd' : '#cbd5e1'),
              borderRadius: 6, padding: '4px 10px'
            }}>
              <input type="checkbox" checked={showWebsiteSoldOnly} onChange={e => setShowWebsiteSoldOnly(e.target.checked)} />
              <Globe size={13} color="#2563eb" /> Website Sold Only ({websiteSoldRowsCount})
            </label>
            <span style={{ marginLeft: 'auto', fontSize: 12.5, color: 'var(--silver-500)' }}>
              {filtered.length} line{filtered.length !== 1 ? 's' : ''}{groupBy ? ` · ${groups.length} groups` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--silver-500)' }}><span className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--silver-500)' }}>
              <Layers size={36} style={{ opacity: .4 }} />
              <div style={{ marginTop: 10 }}>No stock found — purchase items to populate inventory</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>SKU No</th>
                    <th style={th}>Item</th>
                    <th style={th}>Vendor</th>
                    <th style={th}>Size</th>
                    <th style={th}>Collection</th>
                    <th style={thR}>Purchased</th>
                    <th style={thR}>Sold</th>
                    <th style={{ ...th, textAlign: 'center' }}>In Stock</th>
                    <th style={thR}>Purchase ₹</th>
                    <th style={thR}>Total Purch. ₹</th>
                    <th style={thR}>Total Purch. (3%)</th>
                    <th style={thR}>MRP</th>
                    <th style={thR}>Disc</th>
                    <th style={thR}>Sale Price</th>
                    <th style={thR}>Total Sale ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => (
                    <React.Fragment key={g.key || '__all'}>
                      {groupBy && <GroupHeader g={g} />}
                      {!(groupBy && collapsed[g.key]) && g.rows.map(r => (
                        <tr key={r.purchaseDetailID} style={{ opacity: r.remaining <= 0 ? .55 : 1 }}>
                          <td style={td}>
                            <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{r.skuNo || '—'}</code>
                          </td>
                          <td style={td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <strong style={{ color: 'var(--navy-900, #0f172a)' }}>{r.itemName}</strong>
                              {r.websiteSold > 0 && (
                                <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 5px', fontSize: 9.5, fontWeight: 800 }}>
                                  WEBSITE SOLD
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--silver-600)' }}>
                              {r.category || <span className="text-muted">—</span>}
                            </div>
                            {(r.purityCode || r.purchaseNo) && (
                              <div style={{ fontSize: 10.5, color: 'var(--silver-500)' }}>
                                {r.purityCode || ''}{r.purityCode && r.purchaseNo ? ' · ' : ''}{r.purchaseNo || ''}
                              </div>
                            )}
                          </td>
                          <td style={td}>{r.vendor}</td>
                          <td style={td}>{r.productSize || <span className="text-muted">—</span>}</td>
                          <td style={td}>{r.collection || <span className="text-muted">—</span>}</td>
                          <td style={tdR}>{fmt0(r.purchased)}</td>
                          <td style={{ ...tdR, color: r.sold > 0 ? '#b45309' : undefined, fontWeight: r.sold > 0 ? 700 : 400 }}>
                            {fmt0(r.sold)}
                            {r.websiteSold > 0 && (
                              <div style={{ marginTop: 2 }}>
                                <span style={{ background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 800, letterSpacing: '0.02em', display: 'inline-flex', alignItems: 'center', gap: 3 }} title={`${fmt0(r.websiteSold)} piece(s) ordered/sold via website`}>
                                  🌐 {fmt0(r.websiteSold)} web
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}><RemainBadge r={r} /></td>
                          <td style={tdR}>₹{fmt2(r.purchasePrice)}</td>
                          <td style={tdR}>₹{fmt2(r.purchased * Number(r.purchasePrice || 0))}</td>
                          <td style={{ ...tdR, fontWeight: 700, color: '#1e40af', background: '#f0f9ff' }}>₹{fmt2(r.purchased * Number(r.purchasePrice || 0) * 1.03)}</td>
                          <td style={tdR}>₹{fmt2(r.mrp)}</td>
                          <td style={{ ...tdR, color: r.discount > 0 ? '#dc2626' : undefined }}>{r.discount > 0 ? `₹${fmt2(r.discount)}` : '—'}</td>
                          <td style={{ ...tdR, fontWeight: 700, color: 'var(--navy-700, #334155)' }}>₹{fmt2(r.salePrice)}</td>
                          <td style={{ ...tdR, fontWeight: 700, color: '#be185d', background: '#fdf2f8' }}>₹{fmt2(r.purchased * Number(r.salePrice || 0))}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--navy-900, #0f172a)' }}>
                    {(() => {
                      const tdT = { padding: '11px 12px', fontSize: 13, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' };
                      const tdTR = { ...tdT, textAlign: 'right', fontFamily: 'var(--mono)' };
                      return (
                        <>
                          <td colSpan={5} style={tdT}>Grand Total <span style={{ fontWeight: 500, fontSize: 11.5, color: '#94a3b8' }}>({filtered.length} line{filtered.length !== 1 ? 's' : ''})</span></td>
                          <td style={tdTR}>{fmt0(totPurchased)}</td>
                          <td style={{ ...tdTR, color: '#fbbf24' }}>{fmt0(totSold)}</td>
                          <td style={{ ...tdT, textAlign: 'center', color: '#4ade80' }}>{fmt0(totRemaining)}</td>
                          <td style={tdTR}>—</td>
                          <td style={tdTR}>₹{fmt2(purchaseValue)}</td>
                          <td style={{ ...tdTR, color: '#60a5fa' }}>₹{fmt2(purchaseValue * 1.03)}</td>
                          <td style={tdTR}>—</td>
                          <td style={tdTR}>—</td>
                          <td style={tdTR}>—</td>
                          <td style={{ ...tdTR, color: '#f472b6' }}>₹{fmt2(totSaleValue)}</td>
                        </>
                      );
                    })()}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 18px', borderTop: '1px solid var(--border)', background: '#ffffff',
              flexWrap: 'wrap', gap: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'var(--silver-600)' }}>
                <span>
                  Showing <strong>{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong> lines
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--silver-500)' }}>Per page:</span>
                  <select
                    className="form-control"
                    style={{ width: 75, padding: '3px 8px', fontSize: 12.5, height: 32 }}
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="pagination" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button className="page-btn" onClick={() => setPage(1)} disabled={safePage === 1} title="First Page">
                    <ChevronsLeft size={14} />
                  </button>
                  <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} title="Previous Page">
                    <ChevronLeft size={14} />
                  </button>

                  {(() => {
                    let start = Math.max(1, safePage - 2);
                    let end = Math.min(totalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    const pages = [];
                    for (let p = start; p <= end; p++) {
                      if (p >= 1 && p <= totalPages) pages.push(p);
                    }
                    return pages.map(p => (
                      <button
                        key={p}
                        className={`page-btn${safePage === p ? ' active' : ''}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ));
                  })()}

                  <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} title="Next Page">
                    <ChevronRight size={14} />
                  </button>
                  <button className="page-btn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages} title="Last Page">
                    <ChevronsRight size={14} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
